import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import PlayerSparkCard from './PlayerSparkCard';

const { FiSearch, FiSliders, FiChevronDown, FiChevronUp, FiX } = FiIcons;

const POSITIONS = ['All', 'Pitchers', 'Catchers', 'Infielders', 'Outfielders'];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'jersey', label: 'Jersey #' },
  { value: 'hits', label: 'Hits (H)' },
  { value: 'homeRuns', label: 'Home Runs (HR)' },
  { value: 'rbi', label: 'RBI' },
  { value: 'avg', label: 'Batting Avg' },
  { value: 'strikeOuts', label: 'Strikeouts (K)' },
  { value: 'era', label: 'ERA' },
  { value: 'ip', label: 'Innings Pitched' },
  { value: 'wins', label: 'Pitcher Wins' },
];

const INJURY_STATUSES = ['All', 'Active', 'Injured'];

const VIEW_MODES = ['grid', 'list'];

const getStatFromLog = (gameLogs, key, isPitcher) => {
  const group = isPitcher ? 'pitching' : 'hitting';
  const splits = gameLogs?.stats?.find(s => s.group?.displayName === group)?.splits || [];
  const total = splits.reduce((acc, s) => acc + (parseFloat(s.stat?.[key]) || 0), 0);
  return total;
};

const getAvgFromLog = (gameLogs) => {
  const splits = gameLogs?.stats?.find(s => s.group?.displayName === 'hitting')?.splits || [];
  const ab = splits.reduce((a, s) => a + (s.stat?.atBats || 0), 0);
  const h = splits.reduce((a, s) => a + (s.stat?.hits || 0), 0);
  return ab > 0 ? h / ab : 0;
};

const StatBadge = ({ label, value, color }) => (
  <div className="flex flex-col items-center border border-[rgba(101,141,113,0.4)] px-2 py-1 bg-[rgba(0,0,0,0.15)] min-w-[36px]">
    <span className="text-[7px] uppercase tracking-widest text-[#658d71] font-bold">{label}</span>
    <span className="text-[10px] font-black tabular-nums" style={{ color: color || '#a9c9b1' }}>{value ?? '--'}</span>
  </div>
);

const PlayerListRow = ({ player, gameLogs, isPitcher, onClick, index }) => {
  const name = player?.person?.fullName || '--';
  const pos = player?.position?.abbreviation || '--';
  const num = player?.jerseyNumber || '--';

  const hits = getStatFromLog(gameLogs, 'hits', false);
  const hr = getStatFromLog(gameLogs, 'homeRuns', false);
  const rbi = getStatFromLog(gameLogs, 'rbi', false);
  const avg = getAvgFromLog(gameLogs);
  const k = getStatFromLog(gameLogs, isPitcher ? 'strikeOuts' : 'strikeOuts', isPitcher);
  const era = isPitcher ? (gameLogs?.stats?.find(s => s.group?.displayName === 'pitching')?.splits?.[0]?.stat?.era ?? '--') : null;
  const ip = isPitcher ? getStatFromLog(gameLogs, 'inningsPitched', true) : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.02 }}
      onClick={onClick}
      className="flex items-center gap-3 p-2.5 border-b border-[rgba(101,141,113,0.2)] last:border-0 cursor-pointer group hover:bg-[rgba(255,255,255,0.04)] transition-colors"
    >
      <div className="flex items-center gap-2 w-32 shrink-0">
        <span className="text-[8px] font-bold text-[#658d71] w-6 text-right shrink-0">#{num}</span>
        <span className={`text-[8px] font-black uppercase px-1 shrink-0 ${isPitcher ? 'bg-[rgba(196,51,62,0.3)] text-[#e85555]' : 'bg-[rgba(101,141,113,0.3)] text-[#a9c9b1]'}`}>
          {pos}
        </span>
        <span className="text-[11px] font-black uppercase text-white group-hover:text-[#d8ceb3] transition-colors truncate">
          {name.split(' ').slice(1).join(' ') || name}
        </span>
      </div>

      <div className="flex gap-1.5 flex-1 flex-wrap">
        {!isPitcher ? (
          <>
            <StatBadge label="H" value={hits || '–'} />
            <StatBadge label="HR" value={hr || '–'} color={hr > 0 ? '#e85555' : '#658d71'} />
            <StatBadge label="RBI" value={rbi || '–'} color={rbi > 0 ? '#d8ceb3' : '#658d71'} />
            <StatBadge label="AVG" value={avg > 0 ? avg.toFixed(3).replace('0.', '.') : '--'} color={avg >= 0.300 ? '#a9c9b1' : avg >= 0.250 ? '#d8ceb3' : '#e85555'} />
            <StatBadge label="K" value={k || '–'} />
          </>
        ) : (
          <>
            <StatBadge label="ERA" value={era} color={parseFloat(era) <= 3.5 ? '#a9c9b1' : parseFloat(era) > 5 ? '#e85555' : '#d8ceb3'} />
            <StatBadge label="K" value={k || '–'} color="#a9c9b1" />
            <StatBadge label="IP" value={ip?.toFixed(1) || '–'} />
          </>
        )}
      </div>

      <span className="text-[9px] text-[#658d71] group-hover:text-[#a9c9b1] transition-colors shrink-0">→</span>
    </motion.div>
  );
};

const PlayerRosterGrid = ({ roster, gameLogs, onPlayerClick }) => {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [injuryFilter, setInjuryFilter] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  const handleSort = (val) => {
    if (sortBy === val) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(val);
      setSortDir(val === 'era' || val === 'name' || val === 'jersey' ? 'asc' : 'desc');
    }
  };

  const enriched = useMemo(() => {
    return (roster || []).map(p => {
      const isPitcher = p.position?.type === 'Pitcher';
      const log = gameLogs?.find(g => g.id === p.person?.id)?.data;
      const hits = getStatFromLog(log, 'hits', false);
      const hr = getStatFromLog(log, 'homeRuns', false);
      const rbi = getStatFromLog(log, 'rbi', false);
      const avg = getAvgFromLog(log);
      const k = getStatFromLog(log, 'strikeOuts', isPitcher);
      const era = parseFloat(log?.stats?.find(s => s.group?.displayName === 'pitching')?.splits?.[0]?.stat?.era) || 99;
      const ip = getStatFromLog(log, 'inningsPitched', true);
      const wins = getStatFromLog(log, 'wins', true);
      return { player: p, isPitcher, log, hits, hr, rbi, avg, k, era, ip, wins };
    });
  }, [roster, gameLogs]);

  const filtered = useMemo(() => {
    let list = enriched;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(({ player }) => player.person?.fullName?.toLowerCase().includes(q));
    }

    if (filter === 'Pitchers') list = list.filter(({ player }) => player.position?.type === 'Pitcher');
    else if (filter === 'Catchers') list = list.filter(({ player }) => player.position?.type === 'Catcher');
    else if (filter === 'Infielders') list = list.filter(({ player }) => player.position?.type === 'Infielder');
    else if (filter === 'Outfielders') list = list.filter(({ player }) => player.position?.type === 'Outfielder');

    if (injuryFilter === 'Active') list = list.filter(({ player }) => !player.status);
    else if (injuryFilter === 'Injured') list = list.filter(({ player }) => player.status);

    list = [...list].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name': aVal = a.player.person?.fullName || ''; bVal = b.player.person?.fullName || ''; break;
        case 'jersey': aVal = parseInt(a.player.jerseyNumber) || 99; bVal = parseInt(b.player.jerseyNumber) || 99; break;
        case 'hits': aVal = a.hits; bVal = b.hits; break;
        case 'homeRuns': aVal = a.hr; bVal = b.hr; break;
        case 'rbi': aVal = a.rbi; bVal = b.rbi; break;
        case 'avg': aVal = a.avg; bVal = b.avg; break;
        case 'strikeOuts': aVal = a.k; bVal = b.k; break;
        case 'era': aVal = a.era; bVal = b.era; break;
        case 'ip': aVal = a.ip; bVal = b.ip; break;
        case 'wins': aVal = a.wins; bVal = b.wins; break;
        default: aVal = 0; bVal = 0;
      }
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return list;
  }, [enriched, search, filter, injuryFilter, sortBy, sortDir]);

  const activeFilterCount = [
    filter !== 'All', injuryFilter !== 'All', sortBy !== 'name'
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[140px]">
          <SafeIcon icon={FiSearch} size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#658d71]" />
          <input
            type="text"
            placeholder="Search player..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[rgba(0,0,0,0.2)] border border-[#658d71] text-white placeholder-[#658d71] text-[10px] font-bold uppercase tracking-widest pl-8 pr-3 py-1.5 focus:outline-none focus:border-[#a9c9b1] transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#658d71] hover:text-white">
              <SafeIcon icon={FiX} size={12} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors ${showFilters ? 'bg-[#658d71] border-[#a9c9b1] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}
        >
          <SafeIcon icon={FiSliders} size={11} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-[#e85555] text-white text-[8px] font-black px-1 rounded-sm">{activeFilterCount}</span>
          )}
        </button>

        <div className="flex border border-[#658d71] overflow-hidden">
          {VIEW_MODES.map(v => (
            <button
              key={v}
              onClick={() => setViewMode(v)}
              className={`px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-widest transition-colors ${viewMode === v ? 'bg-[#658d71] text-white' : 'text-[#a9c9b1] hover:text-white'}`}
            >
              {v === 'grid' ? '⊞' : '☰'}
            </button>
          ))}
        </div>
      </div>

      {/* Expanded Filters Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border border-[#658d71] bg-[rgba(0,0,0,0.12)] p-3 flex flex-col gap-3"
        >
          {/* Position Filter */}
          <div>
            <p className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold mb-1.5">Position</p>
            <div className="flex gap-1.5 flex-wrap">
              {POSITIONS.map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border transition-colors ${filter === f ? 'bg-[#658d71] border-[#a9c9b1] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Injury Status Filter */}
          <div>
            <p className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold mb-1.5">Status</p>
            <div className="flex gap-1.5 flex-wrap">
              {INJURY_STATUSES.map(s => (
                <button
                  key={s}
                  onClick={() => setInjuryFilter(s)}
                  className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border transition-colors ${injuryFilter === s ? 'bg-[#658d71] border-[#a9c9b1] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Sort Options */}
          <div>
            <p className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold mb-1.5">Sort By</p>
            <div className="flex gap-1.5 flex-wrap">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleSort(opt.value)}
                  className={`flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border transition-colors ${sortBy === opt.value ? 'bg-[rgba(216,206,179,0.15)] border-[#d8ceb3] text-[#d8ceb3]' : 'border-[#658d71] text-[#658d71] hover:text-[#a9c9b1]'}`}
                >
                  {opt.label}
                  {sortBy === opt.value && (
                    <SafeIcon icon={sortDir === 'asc' ? FiChevronUp : FiChevronDown} size={9} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Reset */}
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setFilter('All'); setInjuryFilter('All'); setSortBy('name'); setSortDir('asc'); }}
              className="self-start text-[9px] font-bold uppercase tracking-widest text-[#e85555] border border-[rgba(232,85,85,0.4)] px-2.5 py-1 hover:bg-[rgba(232,85,85,0.1)] transition-colors"
            >
              Reset All Filters
            </button>
          )}
        </motion.div>
      )}

      {/* Results count */}
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-[#658d71] uppercase tracking-widest font-bold">
          {filtered.length} player{filtered.length !== 1 ? 's' : ''}
          {search && ` matching "${search}"`}
        </span>
        {sortBy !== 'name' && (
          <span className="text-[9px] text-[#d8ceb3] uppercase tracking-widest font-bold">
            Sorted: {SORT_OPTIONS.find(o => o.value === sortBy)?.label} {sortDir === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">No players found</p>
      ) : viewMode === 'grid' ? (
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {filtered.map(({ player, log, isPitcher }, i) => (
            <motion.div
              key={player.person?.id || i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.025 }}
            >
              <PlayerSparkCard
                player={player}
                gameLogs={log}
                isPitcher={isPitcher}
                onClick={() => onPlayerClick?.(player.person?.id, player.person?.fullName)}
              />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Player List</span>
            <p className="text-[8px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Click to view full scouting profile</p>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {filtered.map(({ player, log, isPitcher }, i) => (
              <PlayerListRow
                key={player.person?.id || i}
                player={player}
                gameLogs={log}
                isPitcher={isPitcher}
                index={i}
                onClick={() => onPlayerClick?.(player.person?.id, player.person?.fullName)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerRosterGrid;